// Wire contract with peerception-api/api/agent_v2 — PLAN-agent-v2 §10. Keep in sync with the
// backend `schemas.py`; the SSE event names and payload shapes are frozen for the base.

/** Tool-policy modes (#5). */
export type Mode = 'plan' | 'ask' | 'auto' | 'full_auto';
export const MODES: readonly Mode[] = ['plan', 'ask', 'auto', 'full_auto'] as const;

/** Risk class attached to every client tool call by the server (#6). */
export type RiskClass = 'read' | 'navigation' | 'write' | 'destructive';

/** Agents of the Phase C topology (§11, #20). Absent or 'main' on a call = the main agent. */
export const AGENT_NAMES = ['main', 'discovery', 'builder', 'formatting', 'charts', 'verifier'] as const;
export type AgentName = (typeof AGENT_NAMES)[number];
/** i18n keys of the agent labels (`agents.<name>`); unknown agents (e.g. a test `echo` agent) fall back to their raw name. */
export const AGENT_LABELS: Record<AgentName, string> = { main: 'agents.main', discovery: 'agents.discovery', builder: 'agents.builder', formatting: 'agents.formatting', charts: 'agents.charts', verifier: 'agents.verifier' };
/** True for a sub-agent (anything but the main agent). */
export const isSubAgent = (agent?: string | null): agent is string => !!agent && agent !== 'main';

/** Selection snapshot (≤ 30 cells) — §10.1. The backend gives it to the model as a text block ahead of the
 *  message it came with (`sculpt/agent/selection.py`): the address, then each non-empty cell's formula and value;
 *  the address alone when `values` is empty (several areas, or more than 30 cells). */
export interface SelectionSnapshot {
  sheet: string;
  address: string;
  values: unknown[][];
  formulas: string[][];
}

export interface SheetInfo {
  name: string;
  visible: boolean;
  /** Three-state visibility (AUDIT_SPEC §8.2): 'visible' | 'hidden' | 'veryhidden'. */
  visibility?: string;
  used_range: string | null;
  rows: number;
  cols: number;
  /** Per-sheet fingerprint for index freshness (§11.3, #21, `fingerprint.ts`); null when its read failed. */
  fingerprint?: string | null;
}

/** Extra context the user attaches from the "+ context" popover (§10.1 `attachments`). */
export interface RangeAttachment {
  kind: 'range';
  sheet: string;
  address: string;
  values: unknown[][];
  formulas: string[][];
  /** True when the used range exceeded the cap and only its head was attached. */
  truncated: boolean;
}

export interface NamedItemRef {
  name: string;
  refers_to: string;
  scope: 'workbook' | 'sheet';
}

export interface NamesAttachment {
  kind: 'names';
  items: NamedItemRef[];
}

export type ContextAttachment = RangeAttachment | NamesAttachment;

/** Persisted before-image of a write (domain #38f): kept server-side, never in the file. */
export interface UndoRecord {
  id?: number | null;
  journal_id: string;
  tool_name?: string | null;
  sheet: string;
  fingerprint?: string | null;
  ranges: string[];
  before_image: Record<string, unknown>;
  inserted_rows?: Record<string, unknown>[] | null;
  undone: boolean;
  /** The turn of the thread that wrote it: undone, it opens again the audit fixes that turn marked. */
  turn_index?: number | null;
  /** The sheet's live fingerprint differs from the one recorded at write time: do not restore. */
  stale: boolean;
  /** The write was made in another workbook (its file name): not restorable in this one (D13). */
  other_workbook?: string | null;
  created_at?: string | null;
}

/** Client-side cost of building the context (measurement only, logged by the backend `ingest`). */
export interface ClientStats {
  /** Wall time of `buildContext` (selection + sheets + fingerprints), in ms. */
  context_ms: number;
  sheets: number;
  /** Sheets that received a fingerprint (every sheet whose read did not fail). */
  fingerprinted: number;
  /** Cells of the used ranges the fingerprints covered (all of them), summed over the fingerprinted sheets. */
  fingerprint_cells: number;
}

/** Per-turn context sent with every /chat request (#11, §10.1). */
export interface AgentContext {
  workbook_id: string;
  workbook_name: string;
  active_sheet: string | null;
  sheets: SheetInfo[];
  selection: SelectionSnapshot | null;
  locale: string;
  calculation_mode: string | null;
  /** The modeller's Excel (`officePlatform`: `Mac`, `PC`, `OfficeOnline`…), for steps in its menus; null when Office
   *  does not say. */
  platform?: string | null;
  /** Optional user attachments (whole sheet, named ranges); omitted when empty. */
  attachments?: ContextAttachment[];
  /** Measurement of the context build (optional; the backend only logs it). */
  client_stats?: ClientStats;
}

/** A document the user attached to a message (2026-09-10): uploaded to `POST /agent-v2/documents`
 *  before the message, referenced by id in `ChatRequest.document_ids`. Never carries the text. */
export interface DocumentSummary {
  document_id: string;
  filename: string;
  kind: 'pdf' | 'docx' | 'pptx' | 'xlsx' | 'csv' | 'text' | 'image' | string;
  content_type?: string | null;
  size_bytes: number;
  chars: number;
  page_count: number;
  truncated: boolean;
  warnings?: string[];
  /** `processing` while the OCR of scanned pages runs in the background (poll GET /documents/{id}); a processing
   *  document cannot be sent yet. */
  status?: 'ready' | 'processing' | string;
  /** Pages transcribed by the OCR pass (Sonnet 5, option A). */
  ocr_pages?: number;
  thread_id?: string | null;
  created_at?: string | null;
}

/** File extensions sculpt-api reads (`sculpt/documents/extract.py` KIND_BY_EXTENSION). */
export const DOCUMENT_EXTENSIONS = ['pdf', 'docx', 'pptx', 'xlsx', 'xlsm', 'csv', 'tsv', 'txt', 'md', 'markdown', 'json', 'yaml', 'yml', 'png', 'jpg', 'jpeg', 'webp', 'gif'] as const;
/** Documents per message (backend AGENT_V2_DOCS_PER_MESSAGE). */
export const MAX_DOCUMENTS_PER_MESSAGE = 5;
/** Upload cap in bytes (backend AGENT_V2_DOC_MAX_BYTES). */
export const MAX_DOCUMENT_BYTES = 20 * 1024 * 1024;

export interface ChatRequest {
  message: string;
  thread_id: string | null;
  mode: Mode;
  context: AgentContext;
  /** Ids of the documents attached to this message (uploaded beforehand); omitted when none. */
  document_ids?: string[];
  /** The model a new thread runs on (`GET /agent-v2/models`); the server ignores it on an existing thread, which
   *  keeps the model of its first turn (PLAN-model-choice.md D1). Omitted: the server's default. */
  model?: string;
}

/** One client-executed tool call inside a batch (§10.2 `client_tool_call`). */
export interface ClientToolCall {
  tool_call_id: string;
  tool_name: string;
  input: Record<string, unknown>;
  preview: string;
  risk: RiskClass;
  requires_confirmation: boolean;
  /** Agent that issued the call (§11.5); absent or 'main' = the main agent. */
  agent?: string;
  /** The target sheet is on the user's protected list (domain #36): confirmed in every mode. */
  protected?: boolean;
}

export interface ClientToolBatch {
  batch_id: string;
  mode: Mode;
  calls: ClientToolCall[];
  /** `questions`: the batch is a Question card the user answers, not tool calls the pane runs
   *  (sculpt-api PLAN-questions.md). `undo`: the server's undo of an operation — ONE card approves or
   *  declines every call of the batch (sculpt-api operations/undo.py). Absent on every other batch. */
  kind?: 'questions' | 'undo';
  /** `undo` only: what the one card says. */
  summary?: UndoSummary;
  /** The calls depend on one another (an operation's rows inserted, then written in their coordinates; an undo's
   *  restores before the rows it deletes): after a failed call none of the rest runs, each answered `stopped`
   *  (sculpt-api PLAN-operations-fixes.md point 10). */
  stop_on_error?: boolean;
  /** Report the fingerprint of every sheet the batch wrote once it ran (and its circular blocks converged), with the
   *  results: the undo recipe keeps them (point 9). */
  report_fingerprints?: boolean;
  /** `undo` only: each sheet's fingerprint as the operation left it. When one differs now, none of the batch runs:
   *  every call is answered `UndoStale` (point 9). */
  expected_fingerprints?: Record<string, string>;
}

/** What an undo batch takes back, for its single approval card. */
export interface UndoSummary {
  operation: string;
  name: string;
  modelled_on: string;
  restored: number;
  names: number;
  bands: number;
  sheets: string[];
  calls: number;
  left?: string[];
}

/** The Question tool (sculpt-api PLAN-questions.md): the agent asks what neither the request nor the
 *  workbook tells it, and the turn waits on the answers instead of ending. */
export const TOOL_ASK_USER = 'ask_user';
/** The pane's own card for a batch of edits approved as one (`ChatSession.attachApproval`): no tool of the server
 *  has this name; its `input.edits` are the batch's write calls as cards. */
export const APPROVE_BATCH = 'approve_batch';

export interface QuestionOption {
  label: string;
}

export interface AgentQuestion {
  /** Slug the answer comes back under. */
  id: string;
  text: string;
  kind: 'choice' | 'number' | 'text';
  /** One line on why it is asked. */
  why?: string;
  /** `choice` only: 2 to 6 options. */
  options?: QuestionOption[];
  /** `number` only. */
  unit?: string;
  /** The answer Sculpt would pick itself: pre-selected, and what an untouched question sends back. */
  suggestion?: string;
}

export interface QuestionCardInput {
  /** The language the agent committed to writing them in (the user's last message's), e.g. 'en'. The pane draws
   *  nothing from it: its own chrome follows the UI locale. */
  language?: string;
  questions: AgentQuestion[];
}

export interface QuestionAnswer {
  id: string;
  answer: string;
  /** True when the user did not give this answer: the agent's suggestion stands, and it decides. */
  left_to_sculpt: boolean;
}

/** Result of one client tool call, posted back via /resume (§10.4). */
export type ToolResult = Record<string, unknown>;

export interface ToolResultEntry {
  tool_call_id: string;
  result: ToolResult;
  /** How long the add-in took to run the call (ms), for executed calls; the server logs the batch's timing. */
  duration_ms?: number;
}

export interface ResumeRequest {
  thread_id: string;
  batch_id: string;
  results: ToolResultEntry[];
  /** When the batch asked for them (`report_fingerprints`): sheet → its fingerprint once the batch ran. */
  fingerprints?: Record<string, string>;
}

export type SSEEventType =
  | 'thread_id'
  | 'turn'
  | 'text'
  | 'tool_call'
  | 'tool_result'
  | 'plan'
  | 'manual'
  | 'client_tool_call'
  | 'awaiting_client'
  | 'stalled'
  | 'usage'
  | 'error'
  | 'unavailable'
  | 'service_interrupted'
  | 'done';

export interface SSEEvent {
  event: SSEEventType;
  data: string;
}

export interface ToolCallStarted {
  tool_call_id: string;
  tool_name: string;
  /** The call's arguments, so the step can name the sheet it is about. Absent on an older server. */
  input?: Record<string, unknown>;
  agent?: string;
}

// ---- A manual write, declared (`manual` SSE event) -------------------------
// Mirrors sculpt-api `sculpt/agent/manual.py`: `start_manual` declares, before any write, why no operation covers
// the change and the sheets the agent will write on (PLAN-building-operations.md §3.6). A second declaration in the
// same turn adds sheets; the event carries the whole scope each time.
export interface ManualDeclaration {
  why: string;
  sheets: string[];
}

// ---- The accepted Change Plan (`plan` SSE event) ---------------------------
// Mirrors sculpt-api `sculpt/agent/plans/schemas.py` PlanView. It arrives once, between the model's answer
// and the batch the add-in runs, and it is what the pane draws while the writing happens.

export interface PlannedChange {
  sheet: string;
  at: number;
  count: number;
  axis: 'rows' | 'cols';
  removes: boolean;
  why: string;
  /** The band once the plan is written (`70:71`, `E:F`): `at` is in today's coordinates, and the plan's other
   *  insertions above it on the sheet push it down. Empty for a removal; absent from a plan sent before it existed. */
  address?: string;
  /** The place in today's workbook: the row or column an insertion goes in front of, or the band a removal takes
   *  away. What a proposed plan, which wrote nothing, selects. */
  today?: string;
}

/** A formula the plan deliberately left alone: appending a term inside a ratio, a factor or a condition
 *  would compute and mean nothing. The address is what the user acts on (the pane selects it in Excel);
 *  the formula is behind a disclosure, because it is routinely four hundred characters of R1C1. */
export interface DeclinedConsumer {
  sheet: string;
  cell: string;
  label: string;
  reason: string;
  formula: string;
}

/** A formula that reads the object and changes with the plan: one the plan rewrites, or one whose range Excel widens
 *  to take the new object in by itself. Its cells are what the pane selects. */
export interface ConsumerChange {
  sheet: string;
  label: string;
  how: 'rewritten' | 'widened';
  /** Its cells once the plan is written. */
  address: string;
  /** Its cells in today's workbook, which a proposed plan selects. */
  today: string;
}

export interface PlanView {
  operation: 'add' | 'edit' | 'delete';
  name: string;
  modelled_on: string;
  /** What the request called the object, when the workbook has no object by that name and the closest one was
   *  taken for it. Absent from a plan sent before it existed. */
  asked?: string;
  changes: PlannedChange[];
  /** The formulas that change with the plan. Absent from a plan sent before it existed. */
  consumers?: ConsumerChange[];
  declined: DeclinedConsumer[];
  by_hand: string[];
  notes: string[];
  calls: number;
  /** The same view as markdown, for a consumer that cannot draw it. The pane ignores it. */
  markdown?: string;
  /** Plan mode: the plan was checked and shown, and nothing was written. */
  proposed?: boolean;
}

/** `turn` SSE event: the #41 key of the turn now streaming, so a rating can name the exact turn.
 *  Emitted once per run — off the `ingest` update on /chat, replayed from the checkpoint on /resume. */
export interface TurnStarted {
  turn_index: number;
}

export interface ServerToolResult {
  tool_call_id: string;
  tool_name: string;
  result: ToolResult;
  agent?: string;
  /** `structural_audit`: the card to draw. */
  audit?: AuditView;
  /** `drivers`: the card to draw. */
  drivers?: DriversView;
  /** `metrics`: the card to draw. */
  metrics?: MetricsView;
  /** `model_map`: the card to draw. */
  model_map?: ModelMapView;
  /** `reconcile_terms`: the card to draw. */
  terms?: TermsView;
}

/** GET /agent-v2/usage and the `usage` event (sculpt-api PLAN-offers.md §6). The `_usd` names are historical:
 *  amounts are in `currency` (EUR, client price). `budget_usd` is the subscription's included usage (pool) for the
 *  period, `spent_usd` the organization's draw on it. The optional fields are absent on an older server. */
export interface UsageSummary {
  spent_usd: number;
  budget_usd: number;
  remaining_usd: number;
  request_count?: number;
  limit_reached: boolean;
  /** True when the pool is unlimited: budget_usd / remaining_usd are 0 and meaningless. Since `mode`, true only
   *  in mode `unlimited` (an older server also set it without a subscription). */
  unlimited?: boolean;
  /** What the organization runs on (absent on an older server: UsageBar `usageMode` derives it). */
  mode?: UsageMode;
  /** "member": a plain member's view: `spent_usd` / `budget_usd` are their own spend and limit (0 = none), nothing of
   *  the organization's numbers is sent. Absent on an older server: "organization". */
  scope?: 'organization' | 'member';
  currency?: string;
  /** Why a model call is refused when `limit_reached`. */
  reason?: LimitReason | null;
  /** The offer's name. */
  offer?: string | null;
  /** The subscription costs nothing a month (Free): the usage bar offers "Upgrade". */
  free?: boolean;
  subscription_status?: string | null;
  period?: 'trial' | 'month' | 'total';
  period_end?: string | null;
  /** Prepaid usage left, used once the pool is spent. */
  credits?: number;
  user_spent?: number;
  user_cap?: number | null;
  member_cap?: number | null;
}

export type LimitReason = 'limit_reached' | 'user_cap_reached' | 'member_cap_reached' | 'no_subscription' | 'free_full' | 'trial_ended' | 'payment_overdue';

/** `pool`: a subscription with a pool; `unlimited`: a subscription without one; `credits`: no subscription, pack
 *  credits left; `none`: no subscription and no credits (the limit is reached). */
export type UsageMode = 'pool' | 'unlimited' | 'credits' | 'none';

// ---- Threads / history (§10.3) -------------------------------------------

export type DisplayToolStatus = 'ok' | 'error' | 'cancelled' | 'pending';

/** The structural audit as the pane draws it (sculpt-api `catalog/report.py: payload`): the `structural_audit`
 *  tool's artifact, after the model's verdicts (a dismissed finding is not in it). Drawn as `AuditCard`, and the
 *  same object fills the Audit Findings sheet. */
export type AuditSeverity = 'Critical' | 'Warning' | 'Info';

export interface AuditLocation {
  sheet: string;
  /** A1 address without the sheet: a cell or a range. */
  address: string;
}

export interface AuditFindingView {
  id: string;
  severity: AuditSeverity;
  category: 'Error' | 'Construction';
  kind: string;
  title: string;
  locations: AuditLocation[];
  evidence: string;
  impact: number;
  /** The key output it reaches (DSCR, CFADS…), null when none. */
  output: string | null;
  fix: string | null;
  notes: string[];
  hidden: boolean;
  /** What the agent changed, once it wrote this finding's fix and read it back (verdict `fixed`): the finding stays
   *  in the card and the sheet, marked fixed. Null or absent while it is open. */
  fixed?: string | null;
  /** The turn whose writes hold the fix: undoing any of them opens the finding again (sculpt-api `report.reopen`). */
  fixed_in?: number | null;
}

export interface AuditView {
  counts: Record<AuditSeverity, number>;
  outputs: string[];
  output_source: 'labels' | 'results sheets' | 'none';
  /** Excel's setting as read before the audit; null when it was not read. */
  iterative_calculation: boolean | null;
  findings: AuditFindingView[];
}

/** The drivers of a number as the pane draws them (sculpt-api `catalog/drivers.py: summary`): the `drivers` tool's
 *  artifact — every Assumption upstream of the target, the business ones as a tree of the Zones they sit in. */
export interface DriverView {
  id: string;
  /** Its label, or the last headings of its Zone when the label says nothing ("EDF PPA › Stakeholder › Value"). */
  name: string;
  /** Where it is, quoted (`'Sheet'!K79`); `sheet` + `address` (no sheet) are the cells the target reads. */
  at: string;
  cells_at: string;
  sheet: string;
  address: string;
  cells: number;
  total: number;
  value: string;
  unit: string | null;
  category: 'business' | 'timing_date' | 'timing_axis' | 'timing_choice' | 'pasted' | 'lookup';
  /** In the period asked: `dormant` when the number reads it but it does not move it there (a flag at 0, a `MIN` /
   *  `MAX` argument not taken); null or absent when no period was asked. */
  effect?: 'active' | 'dormant' | null;
}

/** One node of the concept tree: a Zone (its heading below its parent's, its role) and its drivers. */
export interface DriverConceptView {
  zone: string | null;
  heading: string;
  path: string[];
  role: string | null;
  /** The first drivers of this node (the card's cap); `more` counts the others. */
  drivers: DriverView[];
  more: number;
  children: DriverConceptView[];
  /** Drivers in this node and below. */
  count: number;
}

/** Entries of one Zone read only by lookups (VLOOKUP, INDEX, MATCH, SUMIFS…): one driver, the table. */
export interface DriverTableView {
  heading: string;
  zone: string | null;
  role: string | null;
  entries: number;
  at: string;
  sheet: string;
  address: string;
}

export interface DriversView {
  /** The target as said back ("c233 "CFADS" …, 2031 → 12 cell(s), monthly periods: …"). */
  subject: string;
  sheet: string;
  period: string | null;
  counts: { business: number; pasted: number; lookup_tables: number; lookup_entries: number; timing: number; in_formulas: number; formula_inputs: number; externals: number; calculations: number };
  concepts: DriverConceptView[];
  tables: DriverTableView[];
  /** Timing by Zone; `drivers` is empty when the group holds more than four (`count`). */
  timing: { heading: string; role: string | null; at: string; sheet: string; address: string; kinds: string[]; count: number; drivers: DriverView[] }[];
  pasted: (DriverView & { pasted_from: string | null })[];
  more_pasted: number;
  in_formulas: { kind: string; text: string; id: string; label: string | null; at: string; sheet: string; address: string }[];
  formula_inputs: { formula: string; id: string; label: string | null; at: string; sheet: string; address: string }[];
  zero_inputs: number;
  externals: string[];
  approximate: string[];
  unresolved: string[];
  loop: boolean;
  /** Calculations walked through, per sheet. */
  calculations: Record<string, number>;
  cells_walked: number;
  /** The dormant drivers again, grouped by what leaves them out (`wake`, the tool's words): they stay in the lists
   *  above, marked by `effect`. Absent from a card drawn before 2026-10-08. */
  dormant?: { wake: string; drivers: DriverView[] }[];
  /** A period was asked but nothing could be told dormant: why (every driver counted as active). */
  unclassified?: string | null;
}

/** One cell or line `metrics` names (sculpt-api `catalog/metrics.py: payload`): `sheet` + bare `address` select it,
 *  `at` is how the answer writes it. */
export interface MetricCellView {
  sheet: string;
  address: string | null;
  at: string | null;
  label: string;
  value: string | null;
}

/** One indicator or key date found: its source, and what the file holds beside it. */
export interface MetricItemView extends MetricCellView {
  key: string;
  /** "DSCR", "equity IRR", "financial close"… */
  title: string;
  /** What tells it apart from another source of the same indicator ("PPA", "Merchant"). */
  variant: string | null;
  /** A period's end, on the row under its start (key dates, tenor). */
  end: MetricCellView | null;
  /** A line's non-zero periods and its minimum / average / maximum — `computed` when the file has no summary cell. */
  stats: { periods: number; first: string | null; last: string | null; min: string; avg: string; max: string;
           min_address: string | null; min_period: string | null; computed: boolean } | null;
  summaries: (MetricCellView & { kind: 'min' | 'avg' | 'max' })[];
  same_values: MetricCellView[];
  also: (MetricCellView & { zone: string })[];
  copies: MetricCellView[];
}

/** The key dates and indicators of a model as the pane draws them: the `metrics` tool's artifact. */
export interface MetricsView {
  families: { key: 'dates' | 'cover' | 'returns' | 'financing'; items: MetricItemView[] }[];
  thresholds: { key: string; name: string; items: MetricCellView[] }[];
  missing: string[];
  selectors: (MetricCellView & { what: 'scenario' | 'sensitivity'; label_address: string })[];
  counts: { indicators: number; dates: number; missing: number };
}

/** One concept of the model map (sculpt-api `catalog/concepts.py: payload`): a Zone at the grain a modeller names. */
export interface MapConceptView {
  id: string;
  /** "output-SPV › SPV › Debt Sized"; "timing (sheet rows)" for a sheet's lines outside any heading. */
  name: string;
  sheet: string;
  /** The Zone's first area without its sheet, to select it; null when the Zone has no area. */
  address: string | null;
  family: string | null;
  role: string | null;
  assumptions: number;
}

/** A contributor in a chain: the lines of it the concept it feeds reads (`weight`), and what feeds it in turn. */
export interface MapStepView extends MapConceptView {
  weight: number;
  input: boolean;
  feeds: MapStepView[];
}

/** The model map as the pane draws it: the `model_map` tool's artifact. */
export interface ModelMapView {
  counts: { concepts: number; links: number; chains: number };
  inventory: { family: string; concepts: MapConceptView[] }[];
  chains: { outputs: string[]; concept: MapConceptView; contributors: MapStepView[]; inputs: MapConceptView[] }[];
}

/** A cell of the model holding a term (sculpt-api `catalog/terms.py: payload`). */
export interface TermPlaceView {
  sheet: string;
  address: string;
  label: string;
  value: string | null;
  /** A result of the model (a sized debt amount), not an Assumption. */
  computed: boolean;
}

export type TermStatus = 'gap' | 'ambiguous' | 'to judge' | 'not in the model' | 'matches' | 'located';

/** A term sheet against the model as the pane draws it: the `reconcile_terms` tool's artifact, the gaps first. */
export interface TermInsteadView {
  sheet: string;
  address: string;
  label: string;
  value: string | null;
}

export interface TermsView {
  rows: {
    term: string;
    family: 'debt' | 'repayment' | 'fees' | 'reserves';
    qualifier: string | null;
    status: TermStatus;
    /** The document's value as written, with its source ("1.35 x [§6]"). */
    document: string | null;
    model: TermPlaceView[];
    /** The model minus the document, as the tool states it. */
    gap: string | null;
    note: string | null;
    /** A term not in the model: what the model holds in its place (a base rate fixed by a swap where the document
     *  names an index). Absent from a card drawn before the server said it. */
    instead?: TermInsteadView[];
  }[];
  not_stated: string[];
  milestones: { name: string; sheet: string; address: string; value: string | null }[];
}

export interface DisplayToolCall {
  tool_call_id: string;
  tool_name: string;
  input: Record<string, unknown>;
  /** How the call ended. `pending` means it genuinely never got an answer (the pane was closed while a
   *  batch ran) — nothing executes while a thread is being read back, so it is not the default. */
  status: DisplayToolStatus;
  /** The error and hint of a call that failed, so a reloaded turn still shows what went wrong. */
  error?: string | null;
  hint?: string | null;
  /** `_changes` of a successful write — what "Applied · 4 cells" is read from. */
  changes?: { sheet: string; address: string; cells: number } | null;
  /** Risk and approval, so a reloaded call is drawn as the same step or card it was live. */
  risk?: RiskClass;
  requires_confirmation?: boolean;
  agent?: string;
  /** `ask_user` only: the answers and the user's own words, so a reopened thread shows the same recap. */
  answers?: QuestionAnswer[] | null;
  message?: string | null;
  /** `structural_audit` only: the card it drew. */
  audit?: AuditView | null;
  /** `drivers` only: the card it drew. */
  drivers?: DriversView | null;
  /** `metrics` only: the card it drew. */
  metrics?: MetricsView | null;
  /** `model_map` only: the card it drew. */
  model_map?: ModelMapView | null;
  /** `reconcile_terms` only: the card it drew. */
  terms?: TermsView | null;
  /** `start_manual` only: the declaration, so a reloaded turn draws the card the live one did. */
  manual?: ManualDeclaration | null;
}

export interface DisplayMessage {
  role: 'user' | 'assistant';
  content: string;
  tool_calls?: DisplayToolCall[] | null;
  /** Documents attached to a user message (2026-09-10). */
  documents?: DisplayDocument[] | null;
  /** #41 key of the turn this message belongs to — what feedback is keyed by. Several assistant messages
   *  of one turn share it. Absent against a server that predates the field. */
  turn_index?: number | null;
  /** The Change Plan this message closed, so a reloaded turn draws the card the live one did. */
  plan?: PlanView | null;
  /** How long the agent took on this message's turn (ms), on the turn's last assistant message, when the add-in
   *  reported it (`recordTurnTime`). */
  duration_ms?: number | null;
}

export type DisplayDocument = Pick<DocumentSummary, 'document_id' | 'filename' | 'kind' | 'page_count' | 'chars' | 'size_bytes' | 'truncated'>;

/** What a resumed thread was started from (PLAN-workbook-identity.md §7): the pane's header. */
export interface ThreadOrigin {
  thread_id: string;
  title: string | null;
  workbook_name: string | null;
}

export interface ThreadSummary {
  thread_id: string;
  title: string | null;
  mode: Mode;
  created_at?: string | null;
  updated_at: string;
  /** Sub-agents involved in the thread (§11.5 history tags), from `history.agents_of`; absent-safe. */
  agents?: string[];
  /** The file the thread belongs to (the "all" view). */
  workbook_name?: string | null;
  /** A turn may be sent on it from the workbook open now: a thread belongs to one workbook (D9). */
  writable: boolean;
  origin?: ThreadOrigin | null;
  /** The model the thread runs on; null on a thread older than the choice. */
  model?: string | null;
}

/** An ancestor workbook's threads created before the fork below it, read-only (D10). */
export interface AncestorThreads {
  name: string;
  forked_at: string;
  threads: ThreadSummary[];
}

/** GET /agent-v2/threads: the open workbook's threads, its ancestors', and with `all` every thread of the user. */
export interface ThreadListing {
  own: ThreadSummary[];
  ancestors: AncestorThreads[];
  all: ThreadSummary[] | null;
}

export interface ThreadDetail {
  thread_id: string;
  title: string | null;
  mode: Mode;
  workbook_id: string | null;
  /** The file the thread belongs to, and whether a turn may be sent on it from the workbook open now. */
  workbook_name?: string | null;
  writable?: boolean;
  origin?: ThreadOrigin | null;
  model?: string | null;
  messages: DisplayMessage[];
  /** The user's ratings of the thread's turns, as last sent. Absent against a server that predates the field. */
  ratings?: TurnRating[];
  pending_batch: ClientToolBatch | null;
  updated_at: string;
}

// ---- Models (PLAN-model-choice.md) ---------------------------------------------

/** One model the modeller can pick (`sculpt/agent/catalog.py`): who hosts it and where the data is processed, as ISO
 *  codes (`EU` included) the pane draws as flags. `allowed`: the user's organization does not refuse it. */
export interface ModelInfo {
  id: string;
  label: string;
  maker: string;
  host: string;
  host_country: string;
  data_location: string[];
  /** 1 (cheap) or 2 (dear), from the list price (sculpt-api `catalog.PRICE_TIERS`). Absent from an older API. */
  price_tier?: number;
  allowed: boolean;
}

/** GET /agent-v2/models: the models offered, and the one a new thread gets when none is named (null: none allowed). */
export interface ModelListing {
  models: ModelInfo[];
  default: string | null;
}

// ---- Skills (sculpt-api sculpt/skills/SPEC.md) ---------------------------------

/** One Skill: a recipe for a whole task the agent reads before starting it (an audit, a DCF…). */
export interface SkillInfo {
  name: string;
  description: string;
}

/** GET /agent-v2/skills: the Skills the agent's prompt lists. A picked one is sent as the message text `/<name> …`. */
export interface SkillListing {
  skills: SkillInfo[];
}

/** 403 detail code: the organization refuses the thread's (or the requested) model. */
export const MODEL_NOT_ALLOWED_CODE = 'model_not_allowed';

// ---- UI state --------------------------------------------------------------

/** Client-side lifecycle of one tool call as rendered in a ToolCallCard. */
export type ToolCardStatus = 'pending' | 'awaiting_confirmation' | 'running' | 'done' | 'error' | 'cancelled';

export interface ToolCard {
  tool_call_id: string;
  tool_name: string;
  input: Record<string, unknown>;
  preview: string;
  risk: RiskClass;
  requires_confirmation: boolean;
  status: ToolCardStatus;
  /** Sheet!address touched by a write (from `_changes`), for the "Show" / highlight actions. */
  target?: { sheet: string; address: string } | null;
  /** Journal entry id when the call was recorded as undoable. */
  journal_id?: string | null;
  /** `_changes` reported by a successful write (what the "Applied · N cells" footer shows). */
  changes?: { sheet: string; address: string; cells: number } | null;
  /** Error message (and hint, if any) of a failed call. */
  error?: string | null;
  /** A server tool's refusal the agent is told to fix and call again (`retryable`): not a failure the user has to
   *  read — it settles as a muted step, like a cancelled one, and the error stays only in its tooltip. */
  retried?: boolean;
  hint?: string | null;
  /** Set once the change was reverted from the journal ("Undo" on the card). */
  undone?: boolean;
  /** Server-executed tools have no client card lifecycle; they only show as indicators. */
  server?: boolean;
  /** Agent that issued the call (§11.5); absent or 'main' = the main agent. */
  agent?: string;
  /** Target sheet is protected (domain #36): shown on the card, always confirmed. */
  protected?: boolean;
  /** Pre-flight of an in-place block write: non-empty cells it would overwrite (#36). */
  overwrite?: { cells: number; address: string; sample: string[] } | null;
  /** `ask_user` only: what the user answered, kept so the settled card shows its recap. */
  answers?: QuestionAnswer[] | null;
  /** `ask_user` only: the words the user typed in the chat box instead of filling the card in. */
  answerMessage?: string | null;
}

/** One piece of an assistant turn, in arrival order: markdown text or a run of tool calls. Text
 *  arriving after tool calls opens a new text segment; tool calls arriving after text land below it. */
export type MessageSegment = { kind: 'text'; markdown: string } | { kind: 'tools'; cards: ToolCard[] };

/** What the turn is waiting on when nothing visible is progressing (drives the spinner label). */
/** `context`: the message is on screen and the workbook is being read for its context, before it is sent.
 *  `index` / `understanding`: the turn waits for the workbook to be re-indexed / for the server to finish
 *  understanding it before it is sent again. */
export type TurnActivity = 'context' | 'model' | 'excel' | 'index' | 'understanding' | 'idle';

/** One rendered chat bubble. */
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  /** Full text of the turn (user text, or the assistant's text segments joined) — kept for
   *  filters/tests; assistant rendering uses `segments`. */
  content: string;
  /** Ordered timeline of the assistant turn (empty for user messages). */
  segments?: MessageSegment[];
  /** Every tool card of the turn, in arrival order (same objects as inside the tools segments). */
  toolCards?: ToolCard[];
  /** Set while the assistant is still streaming or a batch is being executed. */
  pending?: boolean;
  /** Where the turn is blocked while pending: waiting for the model, or executing in Excel. */
  activity?: TurnActivity;
  /** The turn's clock (`turnClock` in ChatSession). A plan on a big model can take a minute and a half, and
   *  a spinner alone does not say whether anything is happening; once the turn ends, how long it took is
   *  worth keeping. Time spent waiting on the user (an approval, a Question) is not the agent's and is
   *  left out. */
  /** When the user sent the message (ms). */
  turnStartedAt?: number;
  /** Time already spent waiting on the user, in ms. */
  userWaitMs?: number;
  /** Since when the turn has been waiting on the user (ms), while it does. */
  userWaitSince?: number;
  /** How long the agent took, in ms, set when the turn ends; reported to the server, which gives it back with
   *  the history (`DisplayMessage.duration_ms`). */
  turnMs?: number;
  /** Batch awaiting the user's approval decisions (cards with awaiting_confirmation). */
  pendingBatch?: ClientToolBatch | null;
  error?: boolean;
  /** Documents attached to a user message (2026-09-10). */
  documents?: DisplayDocument[];
  /** #41 turn key, from the `turn` SSE event or `DisplayMessage.turn_index`. Undefined against an older
   *  server — every consumer must guard, and the per-message thumbs simply stay hidden. */
  turnIndex?: number;
  /** The user's rating of this message's turn, as last sent (`ThreadDetail.ratings` on reload): lights the thumb
   *  and fills the comment form when it reopens. Every bubble of the turn carries it. */
  rating?: TurnRating;
  /** The Change Plan this turn is writing, when one was accepted (`plan` SSE event). */
  plan?: PlanView;
  /** The structural audit of this turn: the latest `structural_audit` result's card. */
  audit?: AuditView;
  /** The workbook's Audit Findings sheet was rewritten from this turn's audit card when the turn ended. */
  auditSheetUpdated?: boolean;
  /** The drivers of a number traced in this turn: the latest `drivers` result's card. */
  drivers?: DriversView;
  /** The key dates and indicators read in this turn: the latest `metrics` result's card. */
  metrics?: MetricsView;
  /** The model map drawn in this turn: the latest `model_map` result's card. */
  model_map?: ModelMapView;
  /** The term sheet checked in this turn: the latest `reconcile_terms` result's card. */
  terms?: TermsView;
  /** The manual write declared in this turn (`manual` SSE event): why, and the sheets it is confined to. */
  manual?: ManualDeclaration;
}

/** GET /agent-v2/access — entitlement of the signed-in user (authenticated, never gated). */
/** Why the gate is closed (sculpt-api PLAN-accounts.md §7). */
export type AccessReason = 'email_unverified' | 'organization_suspended' | 'no_organization' | 'no_subscription' | 'free_full' | 'trial_ended' | 'payment_overdue';

/** The reasons an organization admin fixes on the portal's billing page (PLAN-offers.md §6). */
export const BILLING_REASONS: readonly AccessReason[] = ['no_subscription', 'free_full', 'trial_ended', 'payment_overdue'];

export interface AccessInfo {
  enabled: boolean;
  /** Null when enabled. */
  reason?: AccessReason | null;
  /** The organization's name (the allowance is its). */
  organization?: string | null;
  granted_at: string | null;
  budget_usd: number;
  spent_usd: number;
  remaining_usd?: number;
  unlimited?: boolean;
  period?: 'trial' | 'month' | 'total';
  currency?: string;
  offer?: string | null;
  period_end?: string | null;
  credits?: number;
}

/** Body `detail` of the 403 every gated /agent-v2 route returns without the entitlement. */
export const ACCESS_REQUIRED_DETAIL = 'agent_v2_access_required';

/** Body `detail` of the 429 the credential routes (`POST /token`, `/token/exchange`…) answer after too many failed
 *  attempts (sculpt-api `accounts/deps.py` `rate_limited`). */
export const RATE_LIMITED_DETAIL = 'rate_limited';

/** 429 detail code of `/agent-v2/chat`, `/resume` and `/threads/{id}/continue`, before the stream starts: the user
 *  already has `limit` turns streaming (sculpt-api `agent/turns.py`). */
export const TURNS_IN_PROGRESS_CODE = 'turns_in_progress';

export interface TurnsInProgressDetail {
  code: typeof TURNS_IN_PROGRESS_CODE;
  limit: number;
}

// ---- Satisfaction feedback -------------------------------------------------
// Mirrors `peerception-api/api/agent_v2/feedback/schemas.py`; the slug lists must stay in sync with the
// `FEEDBACK_TAGS` tuple there (an unknown slug is dropped server-side, never rejected).

export const FEEDBACK_NEGATIVE_TAGS = [
  'wrong_formula',
  'misunderstood',
  'too_slow',
  'broke_sheet',
  'wrong_output',
  'too_many_steps',
] as const;
export const FEEDBACK_POSITIVE_TAGS = ['accurate', 'understood', 'fast', 'saved_time'] as const;
export const FEEDBACK_TAGS = [...FEEDBACK_NEGATIVE_TAGS, ...FEEDBACK_POSITIVE_TAGS, 'other'] as const;

export type FeedbackTag = (typeof FEEDBACK_TAGS)[number];
export type FeedbackRating = 'up' | 'down';
export type FeedbackSource = 'popup' | 'message';
export type FeedbackScope = 'thread' | 'turn';
/** `dismissed` records a prompt the user waved away — the denominator that makes the response rate,
 *  and therefore the prompt cadence, measurable. It carries no rating. */
export type FeedbackOutcome = 'rated' | 'dismissed';

/** POST /agent-v2/feedback. `turn_index` absent = the thread-level card; a value = that turn's thumbs. */
export interface FeedbackRequest {
  thread_id: string;
  turn_index?: number | null;
  outcome?: FeedbackOutcome;
  rating?: FeedbackRating | null;
  tags?: FeedbackTag[];
  comment?: string | null;
  source?: FeedbackSource;
  turns_seen?: number | null;
  mode?: Mode | null;
  workbook_id?: string | null;
}

/** One rated turn as `GET /agent-v2/threads/{id}` gives it back (`ThreadDetail.ratings`), and as the pane keeps it
 *  on the turn's messages once sent. */
export interface TurnRating {
  turn_index: number;
  rating: FeedbackRating;
  tags: FeedbackTag[];
  comment: string | null;
}

export interface FeedbackReceipt {
  id: number;
  scope: FeedbackScope;
  thread_id: string;
  turn_index: number | null;
  outcome: FeedbackOutcome;
  rating: FeedbackRating | null;
  tags: FeedbackTag[];
  revision: number;
}

// ---- Report a conversation -------------------------------------------------
// Mirrors sculpt-api `sculpt/feedback/schemas.py` (ReportRequest / ReportReceipt); `REPORT_REASONS` must stay in
// sync with the tuple there (an unknown reason is filed as `other` server-side, never rejected).

export const REPORT_REASONS = ['wrong_result', 'damaged_workbook', 'inappropriate', 'data_concern', 'other'] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

/** POST /agent-v2/reports: the user sends their conversation to the Sculpt team for a person to look at. */
export interface ReportRequest {
  thread_id: string;
  reason: ReportReason;
  comment?: string | null;
}

export interface ReportReceipt {
  id: number;
  thread_id: string;
  reason: ReportReason;
  status: 'open' | 'resolved';
  created_at: string;
}

/** The `detail` of a 429 from POST /agent-v2/reports: the user's reports of the last 24 hours reached the cap. */
export const TOO_MANY_REPORTS = 'too_many_reports';
